import { useEffect, useState, useRef } from "react";
import PageHeader from "../../components/pageHeader";
import { FaChartBar, FaTable, FaFilePdf } from "react-icons/fa";
import LoadingOverlay from "../../components/loadingOverlay";
import AttendanceSummaryTable from "../../components/attendanceSummary/attendanceSummaryTable";
import AbsenceReviewTable from "../../components/attendanceSummary/absenceReviewTable";
import AttendanceSummarySimpleChart from "../../components/attendanceSummary/attendanceSummarySimpleChart";
import AttendanceSummaryDetailChart from "../../components/attendanceSummary/attendanceSummaryDetailChart";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { API_BASE } from "../../config/apiConfig";
import MainContentLayout from "../../components/mainContentLayout";
import AdminAttendanceTabs from "./adminAttendanceTabs";

export default function MonthlyAttendanceSummary() {
  const [summary, setSummary] = useState([]);
  const [totalTrainings, setTotalTrainings] = useState(0);

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [view, setView] = useState("table"); // table | chart
  const [detailedChart, setDetailedChart] = useState(false);
  const [loading, setLoading] = useState(false);

  const token = sessionStorage.getItem("access_token");

  // Export PDF refs
  const tableRef = useRef();
  const absenceTableRef = useRef();
  const simpleChartRef = useRef();
  const detailChartRef = useRef();

  // --------------------------------------------------------
  // 1. LOAD FROM BACKEND API
  // --------------------------------------------------------
  async function loadSummary() {
    if (!token) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/attendance/summary?year=${selectedYear}&month=${selectedMonth}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();
      if (data.success) {
        setSummary(data.summary || []);
        setTotalTrainings(data.totalTrainings || 0);
      } else {
        setSummary([]);
        setTotalTrainings(0);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSummary();
  }, [selectedMonth, selectedYear]);

  // --------------------------------------------------------
  // 2. PDF EXPORT
  // --------------------------------------------------------
  async function handleExportPDF() {
    setLoading(true);
    try {
      const pdf = new jsPDF("l", "mm", "a4");

      const captureAndAddPage = async (ref, title, isFirst = false) => {
        const el = ref.current;
        if (!el) return;

        const canvas = await html2canvas(el, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (!isFirst) pdf.addPage();

        pdf.setFontSize(14);

        const monthName = new Date(selectedYear, selectedMonth).toLocaleString(
          "default",
          { month: "long" }
        );

        const header = `${monthName} ${selectedYear}`;
        const textWidth = pdf.getTextWidth(header);
        pdf.text(header, (pdfWidth - textWidth) / 2, 15);

        pdf.text(title, 10, 25);

        pdf.addImage(imgData, "PNG", 0, 35, pdfWidth, pdfHeight);
      };

      await captureAndAddPage(tableRef, "Attendance Table", true);
      await captureAndAddPage(absenceTableRef, "Absence Review Table");
      await captureAndAddPage(simpleChartRef, "Simple Attendance Chart");
      await captureAndAddPage(detailChartRef, "Detailed Attendance Chart");

      pdf.save("attendance-summary.pdf");
    } finally {
      setLoading(false);
    }
  }

  // --------------------------------------------------------
  // UI
  // --------------------------------------------------------
  return (
    <MainContentLayout>
      <PageHeader title="Attendance Summary" backTo="/dashboard" />
      <AdminAttendanceTabs />

      <div className="p-5 mt-4 space-y-5 max-w-5xl mx-auto w-full">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-4">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="border border-gray-400 rounded px-3 py-2"
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-400 rounded px-3 py-2"
          >
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          {/* Toggle */}
          <button
            onClick={() => setView(view === "table" ? "chart" : "table")}
            className="ml-auto p-2 rounded hover:bg-gray-200"
          >
            {view === "table" ? (
              <FaChartBar size={20} className="text-blue-600" />
            ) : (
              <FaTable size={20} className="text-blue-600" />
            )}
          </button>

          {/* Export */}
          <button onClick={handleExportPDF} className="p-2 rounded hover:bg-gray-200">
            <FaFilePdf size={20} className="text-blue-600" />
          </button>
        </div>

        <p className="mb-4 text-gray-600">
          Total Trainings: <span className="font-semibold">{totalTrainings}</span>
        </p>

        {/* Table View */}
        {view === "table" && (
          <>
            <AttendanceSummaryTable summary={summary} totalTrainings={totalTrainings} />
            <AbsenceReviewTable summary={summary} showTitle={true} />
          </>
        )}

        {/* Chart View */}
        {view === "chart" && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={detailedChart}
                onChange={(e) => setDetailedChart(e.target.checked)}
              />
              <label className="text-sm">Detailed chart</label>
            </div>

            {detailedChart ? (
              <AttendanceSummaryDetailChart summary={summary} />
            ) : (
              <AttendanceSummarySimpleChart summary={summary} />
            )}
          </div>
        )}
      </div>

      {/* Hidden refs for PDF */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <div ref={tableRef} style={{ width: "1200px" }}>
          <AttendanceSummaryTable summary={summary} totalTrainings={totalTrainings} />
        </div>

        <div ref={absenceTableRef} style={{ width: "1200px" }}>
          <AbsenceReviewTable summary={summary} showTitle={false} />
        </div>

        <div ref={simpleChartRef} style={{ width: "1200px", height: "800px" }}>
          <AttendanceSummarySimpleChart summary={summary} />
        </div>

        <div ref={detailChartRef} style={{ width: "1200px", height: "800px" }}>
          <AttendanceSummaryDetailChart summary={summary} />
        </div>
      </div>

      <LoadingOverlay show={loading} text="Loading..." />
    </MainContentLayout>
  );
}
